import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { match } from "ts-pattern";

import { validateNhsNumber } from "@thalamos/common";

import type { SignableSoadAssessmentStep } from "./createSignableSoadAssessment.js";
import { createSignableSoadAssessment } from "./createSignableSoadAssessment.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import { routeFns } from "~/routePaths.js";

/**
 * Whether the element takes text entry, so keystrokes there are input rather
 * than a shortcut.
 */
function isTypingInto(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  return (
    element.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName)
  );
}

/**
 * Shortcut (Option/Alt + Shift + S) that builds a SOAD assessment populated to
 * the point of being signable, so testing the submit, review and sign steps
 * does not require completing a dozen form pages by hand.
 *
 * A testing aid: must only be mounted behind `isDevEnv`. Renders nothing until
 * the chord fires.
 */
export function QuickCreateSoadAssessment() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [nhsNumber, setNhsNumber] = useState("");
  const [steps, setSteps] = useState<SignableSoadAssessmentStep[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // State lags a render behind, so the ref is what stops a second click
  // starting a second run.
  const isRunningRef = useRef(false);

  const close = useCallback(() => {
    if (isRunningRef.current) {
      return;
    }
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Matched on code rather than key: the character Option+Shift+S produces
      // varies by keyboard layout, the physical key does not.
      const isChord =
        event.code === "KeyS" &&
        event.altKey &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey;

      if (!isChord || isTypingInto(document.activeElement)) {
        return;
      }

      event.preventDefault();
      setSteps([]);
      setError(null);
      setIsOpen(true);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const stepLabel = (step: SignableSoadAssessmentStep): string =>
    match(step)
      .with({ kind: "creatingPatient" }, () =>
        t("pages.soadAssessment.quickCreate.steps.creatingPatient"),
      )
      .with({ kind: "findingPatientInPds" }, () =>
        t("pages.soadAssessment.quickCreate.steps.findingPatientInPds"),
      )
      .with({ kind: "creatingAssessment" }, ({ requestId }) =>
        t("pages.soadAssessment.quickCreate.steps.creatingAssessment", {
          requestId,
        }),
      )
      .with({ kind: "completingReportSections" }, () =>
        t("pages.soadAssessment.quickCreate.steps.completingReportSections"),
      )
      .with({ kind: "addingCertificate" }, () =>
        t("pages.soadAssessment.quickCreate.steps.addingCertificate"),
      )
      .with({ kind: "completingCertificate" }, () =>
        t("pages.soadAssessment.quickCreate.steps.completingCertificate"),
      )
      .exhaustive();

  const trimmedNhsNumber = nhsNumber.replace(/\s/g, "");
  const nhsNumberError =
    trimmedNhsNumber.length > 0 && !validateNhsNumber(trimmedNhsNumber)
      ? t("pages.soadAssessment.quickCreate.nhsNumberInvalid")
      : undefined;

  const create = useCallback(() => {
    const runAsync = async () => {
      if (isRunningRef.current) {
        return;
      }
      isRunningRef.current = true;
      setIsRunning(true);
      setSteps([]);
      setError(null);

      try {
        const { patientId, formContextId, requestId } =
          await createSignableSoadAssessment({
            nhsNumber:
              trimmedNhsNumber.length > 0 ? trimmedNhsNumber : undefined,
            onStep: (step) => {
              setSteps((previous) => [...previous, step]);
            },
          });

        setIsOpen(false);
        renderSuccessToast({
          message: t("pages.soadAssessment.quickCreate.created", { requestId }),
        });
        navigate(routeFns.formContextPage(formContextId, patientId));
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        isRunningRef.current = false;
        setIsRunning(false);
      }
    };

    void runAsync();
  }, [navigate, t, trimmedNhsNumber]);

  return (
    <PopupDialog open={isOpen} onClose={close} maxWidth="sm">
      <PopupDialogTitle
        closeDialog={close}
        titleText={t("pages.soadAssessment.quickCreate.title")}
        subtitleText={t("pages.soadAssessment.quickCreate.subtitle")}
        hideCloseButton={isRunning}
      />

      <TextField
        name="quickCreateNhsNumber"
        label={t("pages.soadAssessment.quickCreate.nhsNumberLabel")}
        subtext={t("pages.soadAssessment.quickCreate.nhsNumberSubtext")}
        value={nhsNumber}
        errorMessage={nhsNumberError}
        disabled={isRunning}
        onChange={setNhsNumber}
      />

      {steps.length > 0 && (
        <Stack sx={{ gap: 0.5, mb: 2 }}>
          {steps.map((step, index) => (
            <Typography key={index} variant="body2">
              {isRunning && index === steps.length - 1 ? "..." : "✓"}{" "}
              {stepLabel(step)}
            </Typography>
          ))}
        </Stack>
      )}

      {error !== null && (
        <Banner
          bannerType={BannerList.ERROR}
          title={t("pages.soadAssessment.quickCreate.failed")}
          body={error}
        />
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          label={t("buttonLabels.cancel")}
          disabled={isRunning}
          onClick={close}
        />
        <Button
          label={
            isRunning
              ? t("pages.soadAssessment.quickCreate.creating")
              : t("buttonLabels.create")
          }
          testId="quick-create-soad-assessment-submit"
          disabled={isRunning || nhsNumberError !== undefined}
          onClick={create}
        />
      </Box>
    </PopupDialog>
  );
}
